"use client"

import type React from "react"
import { useState } from "react"

import { useAppContext } from "../../shared/functions/Context"
import { Badge } from "../dashboard/company-dashboard/ui/badge"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../dashboard/company-dashboard/ui/card"
import {  Label } from "../dashboard/company-dashboard/ui/label"
import { Input } from "../dashboard/company-dashboard/ui/input"
import { Button } from "../dashboard/company-dashboard/ui/button"
import { Textarea } from "../dashboard/company-dashboard/ui/textarea"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../dashboard/company-dashboard/ui/tabs"



export default function EmailTestSuite() {
  const [isLoading, setIsLoading] = useState<string | null>(null)
   const { api, store } = useAppContext();
  const [results, setResults] = useState<Record<string, string>>({})


  const updateResult = (key: string, message: string) => {
    setResults((prev) => ({ ...prev, [key]: message }))
  }

  const handleBasicEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading("basic")

    const formData = new FormData(e.currentTarget)
    const to = formData.get("to") as string
    const subject = formData.get("subject") as string
    const message = formData.get("message") as string

    try {
      const response = await api.mail.sendMail([to], "noreply@nkurenkuru-tc.com", [], subject, message)
      updateResult("basic", `✅ Success: ${response.message}`)
    } catch (error) {
      updateResult("basic", `❌ Error: ${error}`)
    } finally {
      setIsLoading(null)
    }
  }

  const handleEmailWithCC = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading("cc")

    const formData = new FormData(e.currentTarget)
    const to = formData.get("to") as string
    const cc = formData.get("cc") as string
    const subject = formData.get("subject") as string
    const message = formData.get("message") as string

    try {
      const response = await await api.mail.sendMailCC([to], cc ? [cc] : [], "noreply@nkurenkuru-tc.com", subject, message)
      updateResult("cc", `✅ Success: ${response.message}`)
    } catch (error) {
      updateResult("cc", `❌ Error: ${error}`)
    } finally {
      setIsLoading(null)
    }
  }

  const handleScorecardEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading("scorecard")

    const formData = new FormData(e.currentTarget)
    const to = formData.get("to") as string
    const subject = formData.get("subject") as string
    const message = formData.get("message") as string

    try {
      const response = await api.mail.scorecardMail(
        to,
        "noreply@nkurenkuru-tc.com",
        subject,
        message,
        undefined, // No PDF for demo
        `<h2>Scorecard Report</h2><p>${message}</p>`,
        "scorecard.pdf",
      )
      updateResult("scorecard", `✅ Success: ${response.message}`)
    } catch (error) {
      updateResult("scorecard", `❌ Error: ${error}`)
    } finally {
      setIsLoading(null)
    }
  }

  const handleEmailWithAttachment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading("attachment")

    const formData = new FormData(e.currentTarget)
    const to = formData.get("to") as string
    const subject = formData.get("subject") as string
    const message = formData.get("message") as string
    const ticketNumber = formData.get("ticketNumber") as string

    try {
      const response = await api.mail.sendMailUpload(
        [to],
        "noreply@nkurenkuru-tc.com",
        [],
        subject,
        message,
        ticketNumber,
      )
      updateResult("attachment", `✅ Success: ${response.message}`)
    } catch (error) {
      updateResult("attachment", `❌ Error: ${error}`)
    } finally {
      setIsLoading(null)
    }
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-2">Firebase Email Test Suite</h2>
        <p className="text-muted-foreground">Test all your Firebase Cloud Functions email endpoints</p>
        <div className="flex justify-center gap-2 mt-4">
          <Badge variant="secondary">Project: nkurenkuru-town-council</Badge>
          <Badge variant="secondary">Region: us-central1</Badge>
        </div>
      </div>

      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="basic">Basic Email</TabsTrigger>
          <TabsTrigger value="cc">Email with CC</TabsTrigger>
          <TabsTrigger value="scorecard">Scorecard</TabsTrigger>
          <TabsTrigger value="attachment">With Attachment</TabsTrigger>
        </TabsList>

        <TabsContent value="basic">
          <Card>
            <CardHeader>
              <CardTitle>Basic Email Test</CardTitle>
              <CardDescription>Test the sendMail function</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBasicEmail} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="basic-to">To Email</Label>
                  <Input id="basic-to" name="to" type="email" placeholder="recipient@example.com" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="basic-subject">Subject</Label>
                  <Input id="basic-subject" name="subject" placeholder="Test Email Subject" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="basic-message">Message</Label>
                  <Textarea id="basic-message" name="message" placeholder="Your email message..." rows={3} required />
                </div>
                <Button type="submit" disabled={isLoading === "basic"} className="w-full">
                  {isLoading === "basic" ? "Sending..." : "Send Basic Email"}
                </Button>
              </form>
              {results.basic && (
                <div className="mt-4 p-3 bg-muted rounded-md">
                  <p className="text-sm">{results.basic}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cc">
          <Card>
            <CardHeader>
              <CardTitle>Email with CC Test</CardTitle>
              <CardDescription>Test the sendMailCC function</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEmailWithCC} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="cc-to">To Email</Label>
                  <Input id="cc-to" name="to" type="email" placeholder="recipient@example.com" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cc-cc">CC Email</Label>
                  <Input id="cc-cc" name="cc" type="email" placeholder="cc@example.com" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cc-subject">Subject</Label>
                  <Input id="cc-subject" name="subject" placeholder="Test Email with CC" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cc-message">Message</Label>
                  <Textarea id="cc-message" name="message" placeholder="Your email message..." rows={3} required />
                </div>
                <Button type="submit" disabled={isLoading === "cc"} className="w-full">
                  {isLoading === "cc" ? "Sending..." : "Send Email with CC"}
                </Button>
              </form>
              {results.cc && (
                <div className="mt-4 p-3 bg-muted rounded-md">
                  <p className="text-sm">{results.cc}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="scorecard">
          <Card>
            <CardHeader>
              <CardTitle>Scorecard Email Test</CardTitle>
              <CardDescription>Test the sendScorecardEmail function with HTML content</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleScorecardEmail} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="scorecard-to">To Email</Label>
                  <Input id="scorecard-to" name="to" type="email" placeholder="recipient@example.com" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="scorecard-subject">Subject</Label>
                  <Input id="scorecard-subject" name="subject" placeholder="Your Scorecard Report" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="scorecard-message">Message</Label>
                  <Textarea
                    id="scorecard-message"
                    name="message"
                    placeholder="Scorecard details..."
                    rows={3}
                    required
                  />
                </div>
                <Button type="submit" disabled={isLoading === "scorecard"} className="w-full">
                  {isLoading === "scorecard" ? "Sending..." : "Send Scorecard Email"}
                </Button>
              </form>
              {results.scorecard && (
                <div className="mt-4 p-3 bg-muted rounded-md">
                  <p className="text-sm">{results.scorecard}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attachment">
          <Card>
            <CardHeader>
              <CardTitle>Email with Attachment Test</CardTitle>
              <CardDescription>Test the sendMailUpload function</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEmailWithAttachment} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="attachment-to">To Email</Label>
                  <Input id="attachment-to" name="to" type="email" placeholder="recipient@example.com" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="attachment-ticket">Ticket Number</Label>
                  <Input id="attachment-ticket" name="ticketNumber" placeholder="TC-2024-001" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="attachment-subject">Subject</Label>
                  <Input id="attachment-subject" name="subject" placeholder="Document Attached" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="attachment-message">Message</Label>
                  <Textarea
                    id="attachment-message"
                    name="message"
                    placeholder="Please find the attached document..."
                    rows={3}
                    required
                  />
                </div>
                <Button type="submit" disabled={isLoading === "attachment"} className="w-full">
                  {isLoading === "attachment" ? "Sending..." : "Send Email with Attachment"}
                </Button>
              </form>
              {results.attachment && (
                <div className="mt-4 p-3 bg-muted rounded-md">
                  <p className="text-sm">{results.attachment}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
